import {
  Popover,
  PopoverContent,
  PopoverHandler,
  Spinner,
} from "@material-tailwind/react";
import { ReactNode, useEffect, useState } from "react";
import SvgIcon from "@/components/common/SvgIcon";
import { Typography } from "@/components/kit";
import classNames from "classnames";
import { useInView } from "react-intersection-observer";

export type TButtonsMenu = {
  button: ReactNode;
  buttonTitle?: string;
  onActionHandle?: (v: string) => void;
  className?: string;
  singleClassName?: string;
  selectedItem?: any;
  stuckColor?: boolean;
  inViewSensetive?: boolean;
  isDisable?: boolean;
  lists: {
    titleClassName?: string;
    loading?: boolean;
    disable?: boolean;
    title: ReactNode;
    icon?: string;
    fill?: string;
    id: string;
    className?: string;
    onClick?: (id: string, selectedItem?: any) => void;
    isAccessible?: boolean | null;
  }[];
};

function ButtonsMenu({
  button,
  lists,
  buttonTitle,
  inViewSensetive = false,
  onActionHandle,
  isDisable,
  className,
  singleClassName,
  selectedItem,
  stuckColor = false,
}: Readonly<TButtonsMenu>) {
  const [open, setOpen] = useState(false);
  const { inView, ref } = useInView();

  useEffect(() => {
    if (!inView && inViewSensetive) {
      setOpen(false);
    }
  }, [inView]);

  useEffect(() => {
    const wheelEvent = () => {
      if (open) {
        setOpen(false);
      }
    };
    document.addEventListener("wheel", wheelEvent);
    return () => {
      document.removeEventListener("wheel", wheelEvent);
    };
  }, [open]);

  return (
    <div
      ref={ref}
      onClick={(e) => e.stopPropagation()}
      className={classNames({
        hidden: !lists.filter((item) => item.isAccessible !== false).length,
      })}
    >
      <Popover
        open={open}
        handler={() => setOpen((pre) => !pre)}
        placement="bottom-end"
      >
        <PopoverHandler>
          <div
            className={classNames({
              "bg-gray-900/20 rounded-lg": open && stuckColor,
            })}
          >
            {button}
          </div>
        </PopoverHandler>
        {!isDisable && (
          <PopoverContent
            className={classNames(
              "px-3 w-[300px] z-[var(--buttons-menu-zIndex)] flex flex-col gap-2",
              className,
            )}
            placeholder=""
          >
            {buttonTitle && (
              <div className="flex px-3 py-2 border-b border-gray-100">
                <Typography variant="Body1-Bold">{buttonTitle}</Typography>
                <div className="flex-1" />
                <SvgIcon
                  iconName="close"
                  fill="fill-gray-600"
                  className="w-4 h-4 cursor-pointer"
                  onClick={() => setOpen(false)}
                />
              </div>
            )}
            {lists
              .filter((item) => item.isAccessible !== false)
              .map((list) => (
                <div
                  onClick={() => {
                    if (list.disable || list.loading) return;
                    list?.onClick?.(list.id, selectedItem);
                    onActionHandle?.(list.id);
                    setOpen(false);
                  }}
                  onKeyDown={() => {
                    list.onClick?.(list.id, selectedItem);
                    setOpen(false);
                  }}
                  key={list.id}
                  className={classNames(
                    "px-3 h-[40px] flex hover:bg-gray-50 rounded-lg gap-2 cursor-pointer items-center ",
                    {
                      "opacity-30 !cursor-not-allowed":
                        list.disable || list.loading,
                    },
                    singleClassName,
                    list.className,
                  )}
                >
                  {list.icon && (
                    <SvgIcon
                      className={`w-4 h-4 ${list.className}`}
                      iconName={list.icon}
                      fill={list.fill}
                    />
                  )}
                  <Typography
                    variant="Body2-Regular"
                    className={list.titleClassName}
                  >
                    {list.title}
                  </Typography>

                  {!list.disable && !!list.loading && (
                    <div className={"flex-shrink-0"}>
                      <Spinner className={"w-4 h-4"} />
                    </div>
                  )}
                </div>
              ))}
          </PopoverContent>
        )}
      </Popover>
    </div>
  );
}

export default ButtonsMenu;
